import { useState, useEffect } from 'react'
import Navbar from '../components/layout/Navbar'
import { mailAgentsApi } from '../api/mailAgents'

const EMPTY_AGENT = {
  name: '',
  smtp_host: 'smtp.zeptomail.com',
  smtp_port: 587,
  smtp_user: 'emailapikey',
  smtp_password: '',
  from_email: '',
  from_name: '',
  is_active: true,
}

export default function MailAgentsPage() {
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_AGENT)
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const loadAgents = async () => {
    setLoading(true)
    try {
      const res = await mailAgentsApi.list()
      setAgents(res.data)
    } catch {
      setMsg({ type: 'error', text: 'Failed to load mail agents.' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAgents() }, [])
  useEffect(() => { if (msg) { const t = setTimeout(() => setMsg(null), 4000); return () => clearTimeout(t) } }, [msg])

  const openAdd = () => {
    setForm(EMPTY_AGENT)
    setEditing(null)
    setShowPassword(false)
    setModalOpen(true)
  }

  const openEdit = (agent) => {
    setForm({
      name: agent.name,
      smtp_host: agent.smtp_host,
      smtp_port: agent.smtp_port,
      smtp_user: agent.smtp_user,
      smtp_password: agent.smtp_password,
      from_email: agent.from_email,
      from_name: agent.from_name,
      is_active: agent.is_active,
    })
    setEditing(agent)
    setShowPassword(false)
    setModalOpen(true)
  }

  const handleSave = async () => {
    const required = ['name', 'smtp_host', 'smtp_user', 'smtp_password', 'from_email', 'from_name']
    const missing = required.find(k => !form[k])
    if (missing) { setMsg({ type: 'error', text: 'All fields except port are required.' }); return }
    setSaving(true)
    try {
      if (editing) {
        await mailAgentsApi.update(editing.id, form)
      } else {
        await mailAgentsApi.create(form)
      }
      setModalOpen(false)
      setMsg({ type: 'success', text: editing ? 'Mail agent updated.' : 'Mail agent created.' })
      loadAgents()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to save.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (agent) => {
    if (agent.campaign_count > 0) return
    if (!window.confirm(`Delete "${agent.name}"? This cannot be undone.`)) return
    try {
      await mailAgentsApi.delete(agent.id)
      setMsg({ type: 'success', text: 'Mail agent deleted.' })
      loadAgents()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to delete.' })
    }
  }

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div style={styles.page}>
      <Navbar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Mail Agents</h1>
            <p style={styles.subtitle}>SMTP sending identities for email campaigns. Each product gets its own agent.</p>
          </div>
          <button style={styles.addBtn} onClick={openAdd}>+ New mail agent</button>
        </div>

        {msg && (
          <div style={{ ...styles.msg, background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4', color: msg.type === 'error' ? '#b91c1c' : '#166534', borderColor: msg.type === 'error' ? '#fecaca' : '#bbf7d0' }}>
            {msg.text}
          </div>
        )}

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : agents.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyTitle}>No mail agents yet</p>
            <p style={styles.emptySub}>Create a mail agent for each product you send campaigns from (SRM, LGSDR, etc.).</p>
            <button style={styles.addBtn} onClick={openAdd}>+ New mail agent</button>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'From', 'SMTP Host', 'Status', 'Campaigns', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map(a => (
                <tr key={a.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.agentName}>{a.name}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.fromName}>{a.from_name}</div>
                    <div style={styles.fromEmail}>{a.from_email}</div>
                  </td>
                  <td style={styles.td}>
                    <code style={styles.code}>{a.smtp_host}:{a.smtp_port}</code>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: a.is_active ? '#dcfce7' : '#f3f4f6', color: a.is_active ? '#166534' : '#6b7280' }}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={styles.count}>{a.campaign_count}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button style={styles.editBtn} onClick={() => openEdit(a)}>Edit</button>
                      <button
                        style={{ ...styles.deleteBtn, opacity: a.campaign_count > 0 ? 0.4 : 1, cursor: a.campaign_count > 0 ? 'not-allowed' : 'pointer' }}
                        onClick={() => handleDelete(a)}
                        disabled={a.campaign_count > 0}
                        title={a.campaign_count > 0 ? `${a.campaign_count} campaign(s) assigned — cannot delete` : 'Delete'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>

      {modalOpen && (
        <div style={styles.overlay} onClick={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editing ? 'Edit mail agent' : 'New mail agent'}</h2>
              <button style={styles.closeBtn} onClick={() => setModalOpen(false)}>✕</button>
            </div>

            <div style={styles.formGrid}>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Agent name *</label>
                <input style={styles.input} value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. SRM Outreach, LGSDR Outreach" />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>From name *</label>
                <input style={styles.input} value={form.from_name} onChange={e => setF('from_name', e.target.value)} placeholder="Brian Menke" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>From email *</label>
                <input style={styles.input} value={form.from_email} onChange={e => setF('from_email', e.target.value)} placeholder="alerts@yourdomain.com" />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>SMTP host *</label>
                <input style={styles.input} value={form.smtp_host} onChange={e => setF('smtp_host', e.target.value)} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>SMTP port</label>
                <input style={styles.input} type="number" value={form.smtp_port} onChange={e => setF('smtp_port', parseInt(e.target.value) || 587)} />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>SMTP username *</label>
                <input style={styles.input} value={form.smtp_user} onChange={e => setF('smtp_user', e.target.value)} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>SMTP password / API key *</label>
                <div style={styles.passwordRow}>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type={showPassword ? 'text' : 'password'}
                    value={form.smtp_password}
                    onChange={e => setF('smtp_password', e.target.value)}
                    placeholder="Paste your ZeptoMail API key"
                  />
                  <button style={styles.showBtn} onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setF('is_active', e.target.checked)} />
                  Active
                </label>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setModalOpen(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save mail agent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  page: { minHeight: '100vh', background: '#fafaf9', fontFamily: 'system-ui, sans-serif' },
  main: { maxWidth: 960, margin: '0 auto', padding: '32px 24px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 600, color: '#1a1a1a' },
  subtitle: { margin: 0, fontSize: 13, color: '#666' },
  addBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  msg: { padding: '10px 14px', borderRadius: 8, border: '1px solid', fontSize: 13, marginBottom: 20 },
  empty: { textAlign: 'center', color: '#888', padding: 48, fontSize: 14 },
  emptyState: { textAlign: 'center', padding: '64px 24px' },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 },
  emptySub: { fontSize: 13, color: '#666', marginBottom: 20 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #e8e8e8' },
  th: { padding: '11px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', background: '#fafaf9', borderBottom: '1px solid #e8e8e8' },
  tr: { borderBottom: '1px solid #f0f0ef' },
  td: { padding: '14px 16px', fontSize: 13, color: '#1a1a1a', verticalAlign: 'middle' },
  agentName: { fontWeight: 600, color: '#1a1a1a' },
  fromName: { fontWeight: 500, color: '#1a1a1a' },
  fromEmail: { color: '#666', fontSize: 12, marginTop: 2 },
  code: { fontSize: 12, background: '#f5f5f3', padding: '2px 6px', borderRadius: 4, color: '#444' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500 },
  count: { fontSize: 13, color: '#555' },
  actions: { display: 'flex', gap: 8 },
  editBtn: { background: 'none', border: '1px solid #d0d0ce', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#333' },
  deleteBtn: { background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#dc2626' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 12, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', padding: 28 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 600, color: '#1a1a1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666', padding: '0 4px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px', marginBottom: 24 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 500, color: '#555' },
  input: { padding: '8px 10px', borderRadius: 7, border: '1px solid #d0d0ce', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  passwordRow: { display: 'flex', gap: 8, alignItems: 'center' },
  showBtn: { background: 'none', border: '1px solid #d0d0ce', borderRadius: 6, padding: '7px 10px', fontSize: 12, cursor: 'pointer', color: '#555', whiteSpace: 'nowrap' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #eee', paddingTop: 20 },
  cancelBtn: { background: 'none', border: '1px solid #d0d0ce', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer', color: '#555' },
  saveBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
}
